import {useState, useEffect, useRef} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, IconButton, Button, styled, FormControl, Select, MenuItem, Backdrop, CircularProgress,} from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { useUser } from '../../context/userContext';
import { getAllInterviewQnsAns, submitInterview } from '../../api/axios';

const BeginButton = styled(Button)(({ theme }) => ({
  border: '1.5px solid #6B3A8F',
  borderRadius: '24px',
  boxShadow: "10px 10px 80px 0px rgba(63, 39, 102, 0.1)", // Converted valid shadow color
  backgroundColor: "#2F272F",
  padding: '7px 40px',
  '&:hover': {
      backgroundColor: '#372D41',
      border: '1.5px solid #6B3A8F',
  },
}));

const EndButton = styled(Button)(({ theme }) => ({
  border: '1px solid #D24A4A',
  borderRadius: '24px',
  boxShadow: "10px 10px 80px 0px rgba(63, 39, 102, 0.1)", // Converted valid shadow color
  backgroundColor: "#2F272F",
  padding: '7px 40px',
  '&:hover': {
      backgroundColor: '#372D41',
      border: '1px solid #D24A4A',
  },
}));

const VirtualInterviewerNew = () => {
    const [selectedQuestion, setSelectedQuestion] = useState("Select a Question")
    const [questionID, setQuestionID] = useState(null)
    const [payloads, setPayloads] = useState([])
    const [isRecording, setIsRecording] = useState(false)
    const [mediaStream, setMediaStream] = useState(null)
    const [showPreview, setShowPreview] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [countdown, setCountdown] = useState(null);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [searchParams] = useSearchParams()
    const domain = searchParams.get("domain");

    const videoRef = useRef("")
    const stopRecordRef = useRef(null)

    const {user}=useUser();

    const navigate = useNavigate()

    const slugify = (str) => {
      return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  }

    const viewInterviewAttempts = () => {
      navigate(`/interview-start-page?shouldPoll=true`)
  } 

    useEffect(() => {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = '';
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, []);

    useEffect(() => {
      const fetchQuestions = async () => {
          let interviewData = []
          try {
              const response = await getAllInterviewQnsAns(domain); 
              const easyQuestions = response.data.filter(item => item.difficulty === "easy");
              const mediumQuestions = response.data.filter(item => item.difficulty === "medium");
              const easyQuestion = easyQuestions[Math.floor(Math.random() * easyQuestions.length)]
              interviewData.push({
                id: 0,
                question: easyQuestion.question,
                answer: easyQuestion.answer,
                video: null,
                videoURL: "",
              })
              const mediumQuestion = mediumQuestions[Math.floor(Math.random() * mediumQuestions.length)]
              interviewData.push({
                id: 1,
                question: mediumQuestion.question,
                answer: mediumQuestion.answer,
                video: null,
                videoURL: "",
              })
              setPayloads(interviewData)
          } catch (error) {
              console.error(`Error fetching all courses: ${error}`)
          }
      };
      fetchQuestions()
      
    }, []);

    const getWebcamStream = async () => {
      stopWebcam()
      if (navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function") {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: 1280,
              height: 720
            },
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                sampleRate: 44100
            }
          });
          if (videoRef.current) {
            videoRef.current.pause()
            videoRef.current.srcObject = null
            videoRef.current.src = ""
            videoRef.current.load()
            videoRef.current.srcObject = stream;
            videoRef.current.muted = true
            videoRef.current.play()
          }
          setMediaStream(stream);
          return stream
        } catch (err) {
          console.error("Access denied or error getting media:", err);
        }
      } else {
        alert("Your browser does not support webcam/microphone access. Please use another browser");
      }

    }

    const startWebcam = async (stream) => {

      try {

        const recorder = new MediaRecorder(stream)

        stopRecordRef.onClick = function () {
          if (isRecording) {
              recorder.stop();
          }
          else {
              recorder.start();
          }
        }

        let chunks = []
    
        recorder.ondataavailable = event => {
          if (event.data.size > 0) {
            chunks.push(event.data)
          }
        }
        
        recorder.onstop = () => { 
          const videoFile = new Blob(chunks, { 
            type: 'video/mp4'
          }) 
          
          chunks = []
          const blobUrl = URL.createObjectURL(videoFile)
          setShowPreview(true)

          setPayloads(prevPayload =>
            prevPayload.map(question => 
              question.id === questionID ? { ...question, video: videoFile, videoURL: blobUrl } : question
            )
          );

          videoRef.current.srcObject = null
          videoRef.current.src = blobUrl
          videoRef.current.muted = false
        }
        
        recorder.start(10)

      } catch (error) {
        console.error("Error accessing webcam", error);
      }
    };

    const stopWebcam = () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => {
          track.stop();
        });
        setMediaStream(null);
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
        videoRef.current.src = "";
        videoRef.current.load();
      }
    };

    const handleStartStopButtonClick = async () => {
        setIsRecording(!isRecording)
        if (isRecording) {
            stopWebcam()
        }
        else {
          const stream = await getWebcamStream()
          if (!stream) return;

          setIsCountingDown(true);
          setCountdown(3);
        
          let current = 3;
          const interval = setInterval(() => {
            current -= 1;
            if (current === 0) {
              clearInterval(interval);
              setIsCountingDown(false);
              setCountdown(null);
              startWebcam(stream); // Now start recording
              setShowPreview(false)
            } else {
              setCountdown(current);
            }
          }, 1000);
        }
    }

    const handleChange = (event) => {
      setQuestionID(payloads[event.target.value].id)
      setSelectedQuestion(payloads[event.target.value].question)
    };

    const handleEndSession = () => {
      const confirmed = window.confirm("Are you sure you want to end the session? All data will be lost.");
      if (confirmed) {
        console.log("Session ended");
        navigate('/interview-start-page');
      }
    };

    const handleInterviewSubmit = async () => {
      if (payloads.some((item) => item.video === null)) {
        window.alert("Please complete all the question before submission")
        return
      }
      setIsUploading(true)

      const formData = new FormData()
      const formDataUpload = new FormData()

      for (let index = 0; index < payloads.length; index++ ) {

        formDataUpload.append("file", payloads[index].video);
        formDataUpload.append("upload_preset", "AlgoOlio");

        const res = await fetch("https://api.cloudinary.com/v1_1/doron2sbm/video/upload", {  // TODO: Set the Cloud Name in the ENV file
          method: "POST",
          body: formDataUpload,
        });
        const response = await res.json();

        // formData.append('videos', payloads[index].video, `video-${index}.mp4`)
        formData.append(`question[${index}]`, payloads[index].question)
        formData.append(`answers[${index}]`, payloads[index].answer)
        formData.append(`blobUrls[${index}]`, response.secure_url)
      }

      try {
        [...formData.entries()].forEach(([key, value]) => {
          console.log(`${key} →`, value);
        });
        const response = await submitInterview(formData)

        console.log(response.data)
      } catch (error) {
        console.error("Error Uploading Videos: ", error)
      } finally {
        setIsUploading(false)
        viewInterviewAttempts()
      }
    }

    return (
        <Box sx={{
            height: '100%',
            width: '100%',
            paddingLeft: '5%',
            paddingTop: '2%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: '#E1E1E6'
        }}>
            {console.log(`User: ${user}`)}
            <Box paddingBottom={5} sx={{width: '100%', display: 'flex', justifyContent: 'space-between',}}>
              {/* <Typography variant='h4' sx={{width: '80%', textAlign:'start'}}>Virtual Interview</Typography> */}
              <Box sx={{width: '100%', display: 'flex', justifyContent: 'space-around'}}>
                  <EndButton variant='contained' onClick={handleEndSession} data-cy='end-session-button'>
                    <Typography>
                        End Session
                    </Typography>
                  </EndButton>
                  <BeginButton variant='contained' onClick={handleInterviewSubmit} data-cy='submit-interview-button'>
                    <Typography sx={{
                        background: "linear-gradient(90deg, #D9BAFF, #D4E8F8, #DAB6E0)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                    }}>
                        Submit Interview
                    </Typography>
                  </BeginButton>
              </Box>
            </Box>
            <Box sx={{width: '60%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'start'}}>
                <Box sx={{width: '100%'}}> 
                  <FormControl fullWidth>
                    <Select
                      data-cy='question-select'
                      value={selectedQuestion}
                      onChange={handleChange}
                      sx={{
                        width: "100%",
                        height: '100%',
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        color: 'white',
                        borderRadius: '24px',
                        border: '1.5px solid #6B3A8F',
                        textAlign: "center",
                        "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: '1.5px solid #6B3A8F',
                        },
                        "& .MuiSelect-icon": {
                          color: "#6B3A8F",      
                          fontSize: "3rem",  
                        }
                      }}
                      renderValue={(selected) =>
                        selected ? (
                          <div
                            style={{
                              width: '99%',
                              height: '100%',
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              display: "block",
                              textAlign: "center",
                              "& .MuiSelectSelect": {
                                display: "flex",
                                justifyContent: "center",
                                textAlign: "center",
                              },
                            }}
                          >
                            {selected}
                          </div>
                        ) : (
                          "Select an option"
                        )
                      }
                    >
                      {console.log(payloads)}
                      {payloads && payloads.map((question, id) => {
                        return <MenuItem 
                                data-cy={`question-item-${slugify(question.question)}`}
                                value={id}
                                key={id}
                                sx={{
                                  whiteSpace: "normal",
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                  paddingX: '5%',
                                  maxWidth: "100%",
                                  display: "flex",
                                  alignItems: "center", // Centers text vertically
                                  justifyContent: "center"
                                }}>
                                  {id + 1}. {question.question}
                                </MenuItem>
                      })}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{position:'relative', width: '100%', height: '80%', display: showPreview ? 'none' : 'block'}}>
                  <video ref={videoRef} autoPlay muted style={{width: '100%', height: '100%'}} />
                </Box>
                {payloads[questionID]?.videoURL && 
                <Box sx={{position:'relative', width: '100%', height: '80%', display: showPreview ? 'block' : 'none'}}>
                    <video src={payloads[questionID].videoURL} controls style={{width: '100%', height: '100%'}}/> {/* OPTIONAL TODO: Customize Recorded Video Controls */}
                </Box> }

                <IconButton onClick={handleStartStopButtonClick} ref={stopRecordRef} data-cy='start-stop-record-button'
                sx={{
                    position: 'absolute', 
                    bottom: '12vh',
                    paddingX: '20px',
                    // width: '7%', 
                    // height: '7%', 
                    borderRadius: '15px', 
                    backgroundColor: 'red',
                    '&:hover': {
                        backgroundColor: 'red',
                    },
                    display: questionID !== null ? 'block' : 'none'
                }}>
                    { isRecording ? <StopCircleIcon sx={{color: 'white', fontSize: 45}}/> : <RadioButtonCheckedIcon sx={{color: 'white', fontSize: 45}}/>}
                </IconButton>

                {isCountingDown && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(0,0,0,0.7)"
                    zIndex={10}
                  >
                    <Typography variant="h1" color="white">
                      {countdown}
                    </Typography>
                  </Box>
                )}

            </Box>

            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isUploading}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress color="inherit" />
                <Typography variant="h6" mt={2}>
                  Uploading your interview...
                </Typography>
              </div>
            </Backdrop>
        </Box>
    )

}

export default VirtualInterviewerNew